import {
  Card,
  CardContent,
  Button,
  Box,
  Grid2 as Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { LoadingButton } from "@mui/lab";

const NotariesGrid = ({
  notariesRow,
  setNotariesRow,
  formik,
  selectedNotary,
  addNotaryError,
  setSelectedNotary,
  removeNotaryLoading,
  setRemoveNotaryLoading,
  addNotaryLoading,
  isExpanded,
  setIsExpanded,
  isCancelExpanded,
  setIsCancelExpanded,
  addedText,
  selectionModel,
  setSelectionModel,
  setFirstTData,
  setSecondTData,
  setNotaryRow,
  setNotaryRequestRows,
  selectedNotaryUserRef,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [loadingRows, setLoadingRows] = useState([]);
  const [gridKey, setGridKey] = useState(0);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const tableData = (row) => {
    let commission_expiry;

    if (typeof row.commission_expiry === "number") {
      commission_expiry = new Date(
        row.commission_expiry * 1000
      ).toLocaleDateString("en-US");
    } else {
      commission_expiry = "N/A";
    }

    const firstData = [
      { label: "Commission Number", value: row.commission_number },
      { label: "Commission Expiry", value: commission_expiry },
      { label: "Registration Date", value: row.created_at },
      { label: "Address", value: row.address },
    ];

    const secondData = [
      { label: "Completed Jobs", value: row.completed_jobs },
      { label: "Pending Jobs", value: row.pending_jobs },
      { label: "Cancelled Jobs", value: row.cancelled_jobs },
      { label: "Total Worked Hours", value: row.total_worked_hours },
    ];

    return { firstData, secondData };
  };

  const notariesColumnsSmall = [
    {
      field: "full_name",
      headerName: "Notary Users",
      flex: 1,
      editable: false,
      renderCell: (params) => {
        return (
          <>
            <Box sx={{ display: "flex", width: "100%", pr: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  // borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                  pb: 0.2,
                  pl: 1,
                  minWidth: "180px",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.90rem",
                    color: "rgba(0, 0, 0, 0.8)",
                    fontWeight: 500,
                  }}
                >
                  {params?.value}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "rgba(0,0,0,0.62)",
                  }}
                >
                  {params.row?.email}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    color: "rgba(0,0,0,0.62)",
                    whiteSpace: "normal",
                  }}
                >
                  {!params?.row?.on_site_registered &&
                  !params?.row?.bn_registered
                    ? "Invitation sent"
                    : params?.row?.on_site_registered &&
                        !params?.row?.bn_registered
                      ? "PR - Registered"
                      : !params?.row?.on_site_registered &&
                          params?.row?.bn_registered
                        ? "BN - Registered"
                        : params?.row?.on_site_registered &&
                            params?.row?.bn_registered
                          ? "Active"
                          : ""}{" "}
                  • {params.row?.created_at}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "rgba(0,0,0,0.62)",
                  }}
                >
                  Online Status: {params.row?.online_status}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "rgba(0,0,0,0.62)",
                  }}
                >
                  Pending Jobs: {params.row?.pending_jobs}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  pb: 0.2,
                  pr: 1,
                }}
              >
                {!params?.row?.on_site_registered &&
                !params?.row?.bn_registered ? (
                  <>
                    {loadingRows[params.row?.id] ? (
                      <CircularProgress size={25} />
                    ) : (
                      <Tooltip title="Cancel Invitation" arrow>
                        <IconButton
                          variant="contained"
                          color="error"
                          onClick={() => {
                            handleCancelNotary(
                              params.row?.email,
                              params.row?.id,
                            );
                          }}
                          className="delete-icon"
                        >
                          <DeleteIcon width={25} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : null}
              </Box>
            </Box>
          </>
        );
      },
    },
  ];

  const notariesColumns = [
    // { field: "id", headerName: "Notary ID", flex: 1 },
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1,
      editable: false,
      valueGetter: (name) => {
        // Capitalize the first letter of each word

        return name
          ?.split(" ")
          ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          ?.join(" ");
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      editable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      editable: false,
      renderCell: (params) => {
        if (!params?.row?.on_site_registered && !params?.row?.bn_registered) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="body2">Invitation sent</Typography>
            </Box>
          );
        } else if (
          params?.row?.on_site_registered &&
          !params?.row?.bn_registered
        ) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="body2">PR - Registered</Typography>
            </Box>
          );
        } else if (
          !params?.row?.on_site_registered &&
          params?.row?.bn_registered
        ) {
          return <Typography variant="body2">BN - Registered</Typography>;
        } else if (
          params?.row?.on_site_registered &&
          params?.row?.bn_registered
        ) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="body2">Active</Typography>
            </Box>
          );
        }
      },
    },
    {
      field: "created_at",
      headerName: "Creation Date",
      flex: 1,
      editable: false,
    },
    {
      field: "online_status",
      headerName: "Online Status",
      flex: 1,
      editable: false,
    },
    {
      field: "pending_jobs",
      headerName: "Pending Jobs",
      flex: 1,
      editable: false,
    },
    {
      field: "action",
      headerName: "",
      width: 50,
      renderCell: (params) => {
        const rowIndex = params.row.id;

        if (!params?.row?.on_site_registered && !params?.row?.bn_registered) {
          return (
            <>
              {loadingRows[rowIndex] ? (
                <CircularProgress size={25} />
              ) : (
                <Tooltip title="Cancel Invitation" arrow>
                  <IconButton
                    variant="contained"
                    color="error"
                    onClick={() => {
                      handleCancelNotary(params.row?.email, rowIndex);
                    }}
                    className="delete-icon"
                  >
                    <DeleteIcon width={25} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        } else {
          return null;
        }
      },
    },
  ];

  const handleCancelNotary = async (email, rowIndex) => {
    try {
      // Set the loading state for the specific row to true
      setLoadingRows((prevLoadingRows) => {
        const newLoadingRows = [...prevLoadingRows];
        newLoadingRows[rowIndex] = true;
        return newLoadingRows;
      });

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/cancel-notary`,
        {
          email,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      if (resp?.data?.success && resp.data?.already_registered) {
        setNotaryRow(resp.data.data);
        setNotaryRequestRows(resp?.data?.notary_requests);
        setLoadingRows((prevLoadingRows) => {
          const newLoadingRows = [...prevLoadingRows];
          newLoadingRows[rowIndex] = false;
          return newLoadingRows;
        });
      } else {
        setNotariesRow((prevNotaryRow) =>
          prevNotaryRow.filter((notary) => notary.email !== email)
        );
        setSelectionModel([]);
        setIsCancelExpanded(false);
        setSelectedNotary(null);
        setLoadingRows((prevLoadingRows) => {
          const newLoadingRows = [...prevLoadingRows];
          newLoadingRows[rowIndex] = false;
          return newLoadingRows;
        });
      }
    } catch (e) {
      setLoadingRows((prevLoadingRows) => {
        const newLoadingRows = [...prevLoadingRows];
        newLoadingRows[rowIndex] = false;
        return newLoadingRows;
      });
    }
  };

  const handleRemoveNotary = async () => {
    try {
      setRemoveNotaryLoading(true);
      const subEmail = selectedNotary?.email;
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/remove-notary`,
        {
          subEmail,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );
      setNotariesRow((prevNotaryRow) =>
        prevNotaryRow.filter((notary) => notary.email !== subEmail)
      );
      setSelectionModel([]);
      setIsCancelExpanded(false);
      setSelectedNotary(null);
      setRemoveNotaryLoading(false);
    } catch (e) {
      setRemoveNotaryLoading(false);
    }
  };

  const getUserData = (params, event) => {
    if (!event.target.closest(".delete-icon")) {
      if (selectionModel.includes(params.id)) {
        setSelectionModel([]);
        setSelectedNotary(null);
        // Force the DataGrid to re-render with a new key
        setGridKey((prevKey) => prevKey + 1);
      } else {
        setSelectionModel([params.id]);
        setSelectedNotary(params.row);
        const data = tableData(params.row);
        setFirstTData(data.firstData);
        setSecondTData(data.secondData);
      }
    }

    setTimeout(
      () =>
        selectedNotaryUserRef.current?.scrollIntoView({
          behavior: "smooth",
        }),
      50,
    );
  };
  return (
    <>
      <Card>
        <CardContent sx={{ p: mobileSize ? "8px !important" : undefined }}>
          <DataGrid
            key={gridKey}
            className="cell"
            sx={{
              WebkitTapHighlightColor: "transparent",
              "& .wrapHeader .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: 1.2,
                textAlign: "center",
              },
              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader": {
                ".MuiDataGrid-menuIcon": {
                  display: "none",
                  pointerEvents: "none",
                },
                cursor: "default",
                paddingLeft: 1,
                paddingRight: 1,
                margin: 0,
                backgroundColor: "#333",
                color: "#fff",
                borderBottom: "1px solid #ccc",
              },
              // Allow sortable column headers to remain interactive
              "& .MuiDataGrid-columnHeader--sortable": {
                pointerEvents: "auto",
                cursor: "pointer",
              },
              "& .MuiDataGrid-columnHeaders": {
                padding: 0, // Adjust as needed
                margin: 0, // Adjust as needed
              },
              "& .tablet-empty-header .MuiDataGrid-columnHeaderTitleContainer":
                {
                  display: "none",
                },
              "& .MuiDataGrid-columnHeadersInner": {
                width: "100%", // Adjust as needed
                padding: 0,
              },
              border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid

              "& .MuiDataGrid-cell": {
                borderColor: tabletSize ? "rgba(0, 0, 0, 0.1)" : "#333333",
                p: tabletSize ? 0 : undefined,
              },
            }}
            rows={notariesRow}
            columns={tabletSize ? notariesColumnsSmall : notariesColumns}
            getRowId={(row) => {
              // Use email as primary ID since it should be unique
              if (row?.email) return row.email;
              // Fall back to id if available and not empty
              if (row?.id && row.id.trim() !== "") return row.id;
              // Use a combination of available fields as last resort
              return `${row?.full_name || "unknown"}-${row?.created_at || "no-date"}`;
            }}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            onRowClick={getUserData}
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
            }}
            rowHeight={tabletSize ? 110 : 36}
            // onCellClick={handleCellClick}
          />

          {/* ADD Notary */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                {isExpanded ? (
                  <Button
                    sx={{ cursor: "pointer", minWidth: "178.17px" }}
                    variant="contained"
                    color="error"
                    size={mobileSize ? "small" : "medium"}
                    startIcon={<RemoveIcon />}
                    onClick={() => {
                      setIsExpanded(false);
                      formik.resetForm();
                    }}
                  >
                    Hide
                  </Button>
                ) : (
                  <Button
                    sx={{ cursor: "pointer", minWidth: "178.17px" }}
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    size={mobileSize ? "small" : "medium"}
                    onClick={() => {
                      setIsExpanded(true);
                    }}
                  >
                    Add Notary
                  </Button>
                )}
              </Box>
              <Accordion
                expanded={isExpanded}
                elevation={0}
                sx={{
                  width: "100%",
                  "&:before": { background: "transparent" },
                }}
              >
                <AccordionSummary
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{
                    borderBottom: "none",
                    cursor: "default",
                    "&.MuiAccordionSummary-root:hover": {
                      cursor: "default",
                    },
                    display: "none", // Hide the summary since we have external buttons
                  }}
                >
                  <Box />
                </AccordionSummary>
                <AccordionDetails>
                  <form noValidate onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="h5" align="center" mb={2}>
                          Add New Notary User
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          name="first"
                          label="First Name"
                          error={
                            formik.touched.first && Boolean(formik.errors.first)
                          }
                          helperText={
                            formik.touched.first && formik.errors.first
                          }
                          value={formik.values.first}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          name="last"
                          label="Last Name"
                          error={
                            formik.touched.last && Boolean(formik.errors.last)
                          }
                          helperText={formik.touched.last && formik.errors.last}
                          value={formik.values.last}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          name="email"
                          label="Email Address"
                          error={
                            formik.touched.email && Boolean(formik.errors.email)
                          }
                          helperText={
                            formik.touched.email && formik.errors.email
                          }
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Grid>
                    </Grid>
                    <Box>
                      <Typography
                        variant="body1"
                        color="error"
                        fontWeight="bold"
                        textAlign="center"
                        sx={{ mt: 2, minHeight: "1.5rem" }}
                      >
                        {addNotaryError}
                      </Typography>
                      <LoadingButton
                        loading={addNotaryLoading}
                        type="submit"
                        variant="contained"
                        size={mobileSize ? "small" : "large"}
                        sx={{ mt: 2 }}
                        fullWidth
                      >
                        Send Invitation
                      </LoadingButton>
                    </Box>
                  </form>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {/* REMOVE SUBUSER */}
            <Box sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                {isCancelExpanded ? (
                  <Button
                    disabled={
                      !selectedNotary || selectedNotary?.status === "pending"
                    }
                    sx={{ cursor: "pointer", minWidth: "178.16px" }}
                    variant="contained"
                    color="error"
                    size={mobileSize ? "small" : "medium"}
                    startIcon={<RemoveIcon />}
                    onClick={() => {
                      setIsCancelExpanded(false);
                    }}
                  >
                    Hide
                  </Button>
                ) : (
                  <Button
                    disabled={
                      !selectedNotary ||
                      selectedNotary?.status === "Invitation Sent" ||
                      selectedNotary?.isAdmin === true
                    }
                    sx={{ cursor: "pointer", minWidth: "178.16px" }}
                    variant="contained"
                    color="error"
                    size={mobileSize ? "small" : "medium"}
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      if (selectedNotary?.isAdmin === true) {
                        return;
                      } else {
                        setIsCancelExpanded(true);
                      }
                    }}
                  >
                    Remove Notary
                  </Button>
                )}
              </Box>
              <Accordion
                expanded={isCancelExpanded}
                elevation={0}
                sx={{
                  width: "100%",
                  "&:before": { background: "transparent" },
                }}
              >
                <AccordionSummary
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{
                    borderBottom: "none",
                    cursor: "default",
                    "&.MuiAccordionSummary-root:hover": {
                      cursor: "default",
                    },
                    display: "none", // Hide the summary since we have external buttons
                  }}
                >
                  <Box />
                </AccordionSummary>
                <AccordionDetails>
                  <form noValidate onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="h5" align="center" mb={2}>
                          Remove Notary User
                        </Typography>
                        <Typography variant="h6" align="center" mb={2}>
                          Are you certain about permanently removing this
                          notary? This action is irreversible.
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          disabled
                          fullWidth
                          name="first"
                          label="First Name"
                          value={selectedNotary?.first_name || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          disabled
                          fullWidth
                          name="last"
                          label="Last Name"
                          value={selectedNotary?.last_name || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          name="email"
                          label="Email Address"
                          disabled
                          value={selectedNotary?.email || ""}
                        />
                      </Grid>
                    </Grid>
                    <Box>
                      <Typography
                        variant="body1"
                        color="error"
                        fontWeight="bold"
                        textAlign="center"
                        sx={{ mt: 2, minHeight: "1.5rem" }}
                      >
                        {addNotaryError}
                      </Typography>
                      <LoadingButton
                        onClick={handleRemoveNotary}
                        loading={removeNotaryLoading}
                        color="error"
                        variant="contained"
                        size={mobileSize ? "small" : "large"}
                        sx={{ mt: 2 }}
                        fullWidth
                      >
                        Confirm Remove Notary User Permanently
                      </LoadingButton>
                    </Box>
                  </form>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Typography
            variant="body1"
            color="error"
            fontWeight="bold"
            textAlign="center"
          >
            {addedText}
          </Typography>
        </CardContent>
      </Card>
    </>
  );
};

export default NotariesGrid;
